import React, { useEffect } from 'react';
import { Grid, Box } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Tag,
  Layers,
  Inventory,
  Money,
  LocalShipping,
  Circle,
  ZoomIn as ZoomInIcon,
} from '@mui/icons-material';
import { DetailModal } from '../../ui/Modal';
import { formatLengthCm, formatWeightGrams } from '../../../utils/localeNumber';
import ImageModal from '../../ui/ImageModal';
import ProductImagePlaceholder from '../../ui/ProductImagePlaceholder';
import { useProductDetailModal, UseProductDetailModalProps } from './ProductDetailModal.hooks';
import { useDataVisibilityPermissions } from '../../../hooks/useDataVisibilityPermissions';
import ProductCatalogStockSection from './ProductCatalogStockSection';
import {
  HeaderSection,
  ProductInfoContainer,
  ImageContainer,
  ProductImage,
  ProductDetails,
  ProductName,
  SkuContainer,
  SkuLabel,
  SkuValue,
  StatusChip,
  InfoCard,
  CardHeader,
  CardTitle,
  CardContent,
  InfoGrid,
  InfoLabel,
  InfoValue,
  DescriptionText,
  TagsContainer,
  TagChip,
  NoTagsText,
  PriceText,
} from './ProductDetailModal.styles';

export interface ProductDetailModalProps extends UseProductDetailModalProps {
  open: boolean;
  className?: string;
}

const ProductDetailModalComponent: React.FC<ProductDetailModalProps> = ({
  open,
  product,
  onEditProduct,
  onClose,
  className,
}) => {
  const theme = useTheme();
  const dataVisibility = useDataVisibilityPermissions();
  const cardHeaderSx = (accentColor: string) => ({
    backgroundColor: alpha(accentColor, theme.palette.mode === 'dark' ? 0.22 : 0.1),
  });
  const cardTitleSx = (accentColor: string) => ({
    color: accentColor,
  });
  const {
    statusColor,
    statusDisplay,
    formattedSellingPrice,
    formattedCostPrice,
    formattedCostPerGram,
    formattedCreatedAt,
    formattedUpdatedAt,
    hasTags,
    hasImage,
    isImageExpanded,
    handleImageClick,
    handleCloseImageExpansion,
  } = useProductDetailModal({
    product,
    onEditProduct,
    onClose,
  });

  const showPriceSection =
    dataVisibility.canShowCostUnitPrice ||
    dataVisibility.canShowCostPerGram ||
    dataVisibility.canShowSalePrice;
  const showSupplierSection = dataVisibility.canShowSupplierName;

  useEffect(() => {}, [product, hasTags]);

  if (!product) return null;

  return (
    <>
      <DetailModal
        open={open}
        onClose={onClose}
        title="Detalle del Producto"
        maxWidth="md"
        className={className}
      >
        {/* Title and status */}
        <HeaderSection>
          <ProductInfoContainer>
            {/* Product image or placeholder */}
            <ImageContainer
              onClick={handleImageClick}
              style={{ cursor: hasImage ? 'pointer' : 'default' }}
            >
              {hasImage && product.imagePath ? (
                <ProductImage src={product.imagePath} alt={product.name} />
              ) : (
                <ProductImagePlaceholder variant="box" size={96} />
              )}
              {hasImage && (
                <Box
                  style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    borderRadius: '50%',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ZoomInIcon sx={{ color: 'common.white', fontSize: '16px' }} />
                </Box>
              )}
            </ImageContainer>

            <ProductDetails>
              <ProductName variant="h5">{product.name}</ProductName>
              <SkuContainer>
                <SkuLabel>SKU:</SkuLabel>
                <SkuValue>{product.code}</SkuValue>
              </SkuContainer>
            </ProductDetails>
          </ProductInfoContainer>

          <StatusChip
            label={statusDisplay}
            size="small"
            className={statusColor}
            icon={<Circle style={{ fontSize: 8 }} />}
          />
        </HeaderSection>

        <Grid container spacing={4}>
          {/* Left column */}
          <Grid item xs={12} md={6}>
            {/* Basic Info */}
            <InfoCard>
              <CardHeader sx={cardHeaderSx('#3b82f6')}>
                <CardTitle sx={cardTitleSx('#3b82f6')}>
                  <Inventory fontSize="small" sx={{ mr: 1 }} />
                  Información Básica
                </CardTitle>
              </CardHeader>
              <CardContent>
                <InfoGrid container spacing={3}>
                  <Grid item xs={6}>
                    <InfoLabel>Código</InfoLabel>
                    <InfoValue>{product.code}</InfoValue>
                  </Grid>
                  <Grid item xs={6}>
                    <InfoLabel>Nombre</InfoLabel>
                    <InfoValue>{product.name}</InfoValue>
                  </Grid>
                  <Grid item xs={6}>
                    <InfoLabel>Fecha de creación</InfoLabel>
                    <InfoValue>{formattedCreatedAt}</InfoValue>
                  </Grid>
                  <Grid item xs={6}>
                    <InfoLabel>Última actualización</InfoLabel>
                    <InfoValue>{formattedUpdatedAt}</InfoValue>
                  </Grid>
                </InfoGrid>

                <Box>
                  <InfoLabel>Descripción</InfoLabel>
                  <DescriptionText>{product.description || 'Sin descripción'}</DescriptionText>
                </Box>
              </CardContent>
            </InfoCard>

            {/* Category Info */}
            <InfoCard>
              <CardHeader sx={cardHeaderSx('#7c3aed')}>
                <CardTitle sx={cardTitleSx('#7c3aed')}>
                  <Layers fontSize="small" sx={{ mr: 1 }} />
                  Categoría
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Box>
                  <InfoLabel>Tipo de Producto</InfoLabel>
                  <InfoValue>
                    {typeof (product as any).type === 'object'
                      ? (product as any).type?.name || 'No especificado'
                      : (product as any).type || 'No especificado'}
                  </InfoValue>
                </Box>

                <Box>
                  <InfoLabel>Etiquetas</InfoLabel>
                  <TagsContainer>
                    {hasTags ? (
                      product.tags!.map((tag: any, index: number) => {
                        const label = typeof tag === 'string' ? tag : (tag?.name ?? '');
                        const tagColor =
                          typeof tag === 'object' && tag?.color ? tag.color : '#3b82f6';
                        return (
                          <TagChip
                            key={index}
                            label={label}
                            size="small"
                            sx={{
                              backgroundColor: `${tagColor}20`,
                              color: tagColor,
                              border: `1px solid ${tagColor}40`,
                            }}
                          />
                        );
                      })
                    ) : (
                      <NoTagsText>Sin etiquetas</NoTagsText>
                    )}
                  </TagsContainer>
                </Box>
              </CardContent>
            </InfoCard>

            {/* Costs */}
            {showPriceSection && (
              <InfoCard>
                <CardHeader sx={cardHeaderSx('#059669')}>
                  <CardTitle sx={cardTitleSx('#059669')}>
                    <Money fontSize="small" sx={{ mr: 1 }} />
                    Precios
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <InfoGrid container spacing={3}>
                    {dataVisibility.canShowCostUnitPrice && (
                      <Grid item xs={6}>
                        <InfoLabel>Costo Unitario</InfoLabel>
                        <InfoValue>{formattedCostPrice}</InfoValue>
                      </Grid>
                    )}
                    {dataVisibility.canShowCostPerGram && (
                      <Grid item xs={6}>
                        <InfoLabel>Costo por gramo</InfoLabel>
                        <InfoValue>{formattedCostPerGram}</InfoValue>
                      </Grid>
                    )}
                    {dataVisibility.canShowSalePrice && (
                      <Grid item xs={6}>
                        <InfoLabel>Precio de Venta</InfoLabel>
                        <PriceText>{formattedSellingPrice}</PriceText>
                      </Grid>
                    )}
                  </InfoGrid>
                </CardContent>
              </InfoCard>
            )}
          </Grid>

          {/* Right column */}
          <Grid item xs={12} md={6}>
            {/* Properties */}
            <InfoCard>
              <CardHeader sx={cardHeaderSx('#d97706')}>
                <CardTitle sx={cardTitleSx('#d97706')}>
                  <Tag fontSize="small" sx={{ mr: 1 }} />
                  Propiedades
                </CardTitle>
              </CardHeader>
              <CardContent>
                <InfoGrid container spacing={3}>
                  <Grid item xs={6}>
                    <InfoLabel>Material</InfoLabel>
                    <InfoValue>
                      {typeof (product as any).material === 'object'
                        ? (product as any).material?.name || 'No especificado'
                        : (product as any).material || 'No especificado'}
                    </InfoValue>
                  </Grid>
                  <Grid item xs={6}>
                    <InfoLabel>Peso</InfoLabel>
                    <InfoValue>{formatWeightGrams(product.weight)}</InfoValue>
                  </Grid>
                  <Grid item xs={6}>
                    <InfoLabel>Largo</InfoLabel>
                    <InfoValue>{formatLengthCm(product.length)}</InfoValue>
                  </Grid>
                  <Grid item xs={6}>
                    <InfoLabel>Talla</InfoLabel>
                    <InfoValue>{product.diameter || 'No especificado'}</InfoValue>
                  </Grid>
                  <Grid item xs={6}>
                    <InfoLabel>Color</InfoLabel>
                    <InfoValue>{product.color || 'No especificado'}</InfoValue>
                  </Grid>
                  <Grid item xs={6}>
                    <InfoLabel>Código Proveedor</InfoLabel>
                    <InfoValue>{product.workshopCode || 'No especificado'}</InfoValue>
                  </Grid>
                </InfoGrid>
              </CardContent>
            </InfoCard>

            {/* Supplier */}
            {showSupplierSection && (
              <InfoCard>
                <CardHeader sx={cardHeaderSx('#4f46e5')}>
                  <CardTitle sx={cardTitleSx('#4f46e5')}>
                    <LocalShipping fontSize="small" sx={{ mr: 1 }} />
                    Proveedor
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {dataVisibility.canShowSupplierName && (
                    <Box>
                      <InfoLabel>Nombre del Proveedor</InfoLabel>
                      <InfoValue>
                        {typeof (product as any).supplier === 'object'
                          ? (product as any).supplier?.name || 'No especificado'
                          : (product as any).supplier || 'No especificado'}
                      </InfoValue>
                    </Box>
                  )}
                </CardContent>
              </InfoCard>
            )}
          </Grid>
        </Grid>

        <ProductCatalogStockSection
          productId={product.id}
          open={open}
          accentColor="#0f766e"
          cardHeaderSx={cardHeaderSx}
          cardTitleSx={cardTitleSx}
        />
      </DetailModal>

      <ImageModal
        isOpen={isImageExpanded}
        onClose={handleCloseImageExpansion}
        imageUrl={product.imagePath || ''}
        altText={product.name}
      />
    </>
  );
};

export default ProductDetailModalComponent;
